import {
  Button,
  ButtonWithPopout,
  ChevronLeftIcon,
  GearIcon,
  HelpIcon,
  Icon,
  InfoIcon,
  LogoutIcon,
  MenuIcon,
  ShareIcon,
  SwapIcon,
  Theme,
  ThemeName,
  UserIcon,
} from '@taopix/taopix-design-system';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useExperience} from '../Experience/ExperienceProvider';
import {Lang, Localisation, ViewMode} from '@taopix/taopix-core';
import SaveButton from '../Save/SaveButton';
import classNames from 'classnames';
import useSettings from '../../Hooks/useSettings';
import {ROUTE_ASSISTANT} from '../../Constants';
import {useChangeProductContext} from '../Context/ChangeProduct/ChangeProductContext';
import useViewMode from '../../Hooks/useViewMode';
import {AuthSettings} from '@taopix/taopix-core/dist/types/types';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';

export enum AppMenuItem {
  SignIn = 'signin',
  Language = 'language',
  SignOut = 'signout',
  Register = 'register',
  Save = 'save',
  Share = 'share',
  ChangeProduct = 'change',
  About = 'about',
  Help = 'help',
  Exit = 'exit',
}

export type AppMenuProps = {
  authentication: AuthSettings;
  showSave: boolean;
  showShare: boolean;
  onClickItem: (value: AppMenuItem) => void;
};

const AppMenu = ({authentication, onClickItem, ...props}: AppMenuProps) => {
  const {experience} = useExperience();
  const [viewMode] = useViewMode();
  const appMenuPerms = experience.SETTINGS.appMenu;
  const inAssistant = window.location.hash.includes(ROUTE_ASSISTANT);
  const {t} = useTranslation([
    '*',
    ...(appMenuPerms.share && !inAssistant ? ['shareproject'] : []),
  ]);
  const settings = useSettings();
  const changeProduct = useChangeProductContext();
  const ui = useUiState();

  const onClick = (value: AppMenuItem) => {
    onClickItem(value);
  };

  const buttonRef = React.useRef<HTMLDivElement>(null);

  const showChangeProductButton =
    appMenuPerms.changeProduct && changeProduct.state.canChangeProduct && !inAssistant;

  let saveButton: React.ReactElement = <></>;
  let changeProductButton: React.ReactElement = <></>;
  let shareButton: React.ReactElement = <></>;
  let showProjectSection = false;
  let signInButton: React.ReactElement = <></>;
  let registerButton: React.ReactElement = <></>;
  let showAccountSection = false;

  const showAboutButton = appMenuPerms.about && settings.state.urls.about !== '';
  const showHelpButton = appMenuPerms.help && settings.state.urls.help !== '';
  const showExitButton = appMenuPerms.exitButton && ui.state.isSmallScreen;

  const exitLabel = (): string => {
    if (appMenuPerms.exitButtonLabel.exitButtonLabel === 'cust') {
      return Localisation.getLocaleString(
        Lang.getCode(),
        appMenuPerms.exitButtonLabel.customExitButtonLabel
      );
    } else if (appMenuPerms.exitButtonLabel.exitButtonLabel === 'bts') {
      return t('str_ButtonBackToSite');
    } else {
      return t('*:str_ButtonExit');
    }
  };

  if (appMenuPerms.saveButton && ViewMode.Preview !== viewMode) {
    showProjectSection = true;
    saveButton = (
      <SaveButton
        label={t('str_ButtonSave')}
        corners={'square'}
        size={'medium'}
        stacked={false}
        onProjectSaved={() => {
          onClick(AppMenuItem.Save);
        }}
      />
    );
  }

  if (showChangeProductButton) {
    showProjectSection = true;
    changeProductButton = (
      <Button
        label={t('str_ButtonChangeProduct')}
        buttonStyle={'standard'}
        corners={'square'}
        labelAlignment={'left'}
        startIcon={<SwapIcon />}
        onClick={() => onClick(AppMenuItem.ChangeProduct)}
      />
    );
  }

  if (appMenuPerms.share && !inAssistant) {
    showProjectSection = true;
    shareButton = (
      <Button
        label={t('str_ButtonShare')}
        startIcon={<ShareIcon />}
        buttonStyle={'standard'}
        corners={'square'}
        labelAlignment={'left'}
        onClick={() => onClick(AppMenuItem.Share)}
      />
    );
  }

  if (!authentication?.userRegistered && appMenuPerms?.signInSignOut) {
    showAccountSection = true;
    signInButton = (
      <Button
        label={t('str_ButtonSignIn')}
        buttonStyle={'standard'}
        corners={'square'}
        labelAlignment={'left'}
        onClick={() => onClick(AppMenuItem.SignIn)}
        startIcon={<UserIcon />}
      />
    );
  } else if (authentication?.userRegistered) {
    showAccountSection = true;
    signInButton = (
      <>
        {'' !== (authentication?.userName || '') && (
          <Theme
            name={ThemeName.Container}
            element="p"
            allowCorners={false}
            className={'flex px-sm py-xs rounded-full text-small m-sm mb-xs opacity-80'}
          >
            <Icon icon={<UserIcon />} size="xsmall" className={'mr-xs'} />
            {authentication?.userName}
          </Theme>
        )}
        {appMenuPerms?.signInSignOut && (
          <Button
            label={t('str_LabelLogout')}
            buttonStyle={'standard'}
            corners={'square'}
            labelAlignment={'left'}
            onClick={() => onClick(AppMenuItem.SignOut)}
            startIcon={<LogoutIcon />}
          />
        )}
      </>
    );
  }

  if (
    !authentication?.userRegistered &&
    authentication?.canCreateAccounts &&
    appMenuPerms.register
  ) {
    showAccountSection = true;
    registerButton = (
      <>
        <Button
          label={t('str_ButtonRegister')}
          buttonStyle={'standard'}
          corners={'square'}
          labelAlignment={'left'}
          onClick={() => onClick(AppMenuItem.Register)}
          startIcon={<UserIcon />}
        />
      </>
    );
  }

  const sectionClasses = classNames(
    'flex',
    'flex-col',
    'after:w-full',
    'after:border-b',
    'after:border-b-themeTextColour',
    'after:opacity-10',
    'last:after:border-b-0'
  );

  const showAppMenu =
    showProjectSection ||
    showAccountSection ||
    appMenuPerms.language ||
    showAboutButton ||
    showHelpButton ||
    false;

  if (!showAppMenu) {
    return <></>;
  }

  return (
    <div id="tds-app-menu" className="flex h-full p-sm" role="presentation" ref={buttonRef}>
      <ButtonWithPopout
        type="menu"
        buttonProps={{
          id: 'tds-app-menu-button',
          startIcon: <MenuIcon />,
          buttonStyle: 'standard',
          size: ui.state.isSmallScreen ? 'small' : 'medium',
          label: t('*:str_ButtonMenu'),
          hideLabel: true,
          className: 'self-center offsetFocus',
        }}
        popoutProps={{
          displayMode: 'positioned',
          offset: {x: 0, y: 5},
          shadowRoot: document.querySelector('#root')?.shadowRoot,
          className: 'min-w-[160rem] max-w-bounds flex-col',
          theme: 'tpx-theme-header',
          themeTip: 'Header',
          contentTheme: ThemeName.Popouts,
          'aria-label': t('*:str_ButtonMenu'),
        }}
      >
        {showAccountSection && (
          <section className={sectionClasses}>
            {signInButton}
            {registerButton}
          </section>
        )}

        {showProjectSection && (
          <section className={sectionClasses}>
            {saveButton}
            {changeProductButton}
            {shareButton}
          </section>
        )}

        {appMenuPerms.language && (
          <section className={sectionClasses}>
            <Button
              label={t('str_ButtonLanguage')}
              buttonStyle={'standard'}
              corners={'square'}
              labelAlignment={'left'}
              onClick={() => onClick(AppMenuItem.Language)}
              startIcon={<GearIcon />}
            />
          </section>
        )}

        {(showAboutButton || showHelpButton) && (
          <section className={sectionClasses}>
            {showAboutButton && (
              <Button
                label={t('*:str_TitleAbout')}
                buttonStyle={'standard'}
                corners={'square'}
                labelAlignment={'left'}
                startIcon={<InfoIcon />}
                onClick={() => onClick(AppMenuItem.About)}
              />
            )}
            {showHelpButton && (
              <Button
                label={t('*:str_TitleHelp')}
                buttonStyle={'standard'}
                corners={'square'}
                labelAlignment={'left'}
                startIcon={<HelpIcon />}
                onClick={() => onClick(AppMenuItem.Help)}
              />
            )}
          </section>
        )}

        {showExitButton && (
          <section className={sectionClasses}>
            <Button
              label={exitLabel()}
              buttonStyle={'standard'}
              corners={'square'}
              labelAlignment={'left'}
              startIcon={<ChevronLeftIcon />}
              onClick={() => onClick(AppMenuItem.Exit)}
            />
          </section>
        )}
      </ButtonWithPopout>
    </div>
  );
};

export default AppMenu;
